import { formatLog } from './utils';
import { logger } from './logger.instance';

/**
 * Represents a single log, in a stream of logs.
 */
export interface BufferedLogEntry {
  /**
   * The moment in time, the log was added to the stream.
   */
  timestamp: Date;

  /**
   * The message to be logged at a later point in time.
   */
  messageParts: unknown[];
}

/**
 * Configuration options for the BufferedLogger instance.
 */
export interface BufferedLoggerOptions {
  /**
   * The maximum age of an inactive stream in seconds before it's deleted.
   * A stream is considered inactive if its last log entry is older than this value.
   * @default 60 (1 minute)
   */
  maxAgeSeconds?: number;

  /**
   * The maximum number of log entries to store per stream. When this limit is exceeded, the oldest entry is removed.
   * @default 256
   */
  maxStreamSize?: number;
}

/**
 * A logger that can be used to log a whole stream of buffered messages/logs, after the fact,
 * e.g., only if a certain condition is true.
 */
export class BufferedLogger {
  private readonly logStreams = new Map<string, BufferedLogEntry[]>();
  private readonly maxAgeInMs: number;
  private readonly maxStreamSize: number;

  constructor(options: BufferedLoggerOptions = {}) {
    this.maxAgeInMs = (options.maxAgeSeconds ?? 60) * 1000;
    this.maxStreamSize = options.maxStreamSize ?? 256;
  }

  /**
   * Adds a new {@link BufferedLogEntry} to the stream with the {@link streamId}, if present.
   *
   * @param streamId      Unique ID of the stream the new log should be added to.
   * @param messageParts  Log that should be added to the stream with the {@link streamId}.
   */
  log(streamId: string, ...messageParts: unknown[]): void {
    this.cleanupInactiveStreams();

    const isBrowserDebugModeEnabled = logger.isDebugModeEnabled();
    if (!streamId && !isBrowserDebugModeEnabled) {
      return;
    } else if (isBrowserDebugModeEnabled) {
      logger.debug(...messageParts);
      return;
    }

    if (!this.logStreams.has(streamId)) {
      this.logStreams.set(streamId, []);
    }

    const stream = this.logStreams.get(streamId);

    stream.push({
      timestamp: new Date(),
      messageParts: messageParts
    });

    while (stream.length > this.maxStreamSize) {
      stream.shift();
    }
  }

  /**
   * Deletes entire stream that are considered inactive, i.e., the last log entry is older than the {@link maxAgeInMs}.
   */
  private cleanupInactiveStreams(): void {
    const cutoffTime = Date.now() - this.maxAgeInMs;

    // @ts-ignore -> MapIterator "not compatible" with es2015 = fake news (target of logging lib = es2022)
    for (const [streamId, logEntries] of this.logStreams.entries()) {
      if (logEntries.length > 0) {
        const lastEntryTime = logEntries.at(-1).timestamp.getTime();
        if (lastEntryTime < cutoffTime) {
          this.logStreams.delete(streamId);
        }
      }
    }
  }

  /**
   * Formats the entire stream of {@link BufferedLogEntry}s to a list of strings,
   * starting with the timestamp of when the log was originally added to the stream,
   * writes them to the console and deletes the stream.
   *
   * @param streamId          Unique ID of the stream that should be converted into formatted strings.
   * @param message           Message displayed above and below the log output, e.g.,
   *                          `<----- START - <message> -----> ... <----- END - <message> ----->`.
   * @param [timeoutInMs=10]  Optional timeout in milliseconds, this method will wait before starting to flush to the
   *                          console, and before START and END message, to not mix up unbuffered with buffered logs.
   */
  flushToConsole(streamId: string, message: string, timeoutInMs = 10): void {
    const stream = this.logStreams.get(streamId) ?? [];

    setTimeout(() => {
      console.error(`<----- START - ${message} -----> `);

      setTimeout(() => {
        stream
          .map((entry: BufferedLogEntry) => formatLog(entry.messageParts, entry.timestamp))

          .forEach((logMessage) => console.log(logMessage));

        setTimeout(() => {
          console.error(`<----- END - ${message} -----> `);
        }, timeoutInMs);

        this.logStreams.delete(streamId);
      }, timeoutInMs);
    }, timeoutInMs);
  }
}
